import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import { categories, household, navigate, goBack, loadCategories, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import { CATEGORY_ICON_CHOICES, CATEGORY_FALLBACK_ICON, categoryIconName } from '../lib/labels.js';
import Header from '../components/Header.jsx';
import Modal from '../components/Modal.jsx';
import Icon from '../components/Icon.jsx';
import BackButton from '../components/BackButton.jsx';

function CategoryEditModal({ editingCategory, onClose, onSaved }) {
  const isCreate = !editingCategory.id;
  const [name, setName] = useState(editingCategory.name || '');
  // Normalize via categoryIconName so editing a legacy emoji category
  // preselects its mapped Material Symbol; saving then upgrades the stored
  // value to the icon name (the mapping keeps unedited rows rendering).
  const [icon, setIcon] = useState(
    editingCategory.icon ? categoryIconName(editingCategory.icon) : CATEGORY_FALLBACK_ICON
  );
  const [isShared, setIsShared] = useState(!!editingCategory.is_shared);
  const [isOutdoor, setIsOutdoor] = useState(!!editingCategory.is_outdoor);
  const [saving, setSaving] = useState(false);

  // onSaved() resets the parent's edit mode — but a save/delete request has
  // no AbortController, so it can still resolve after the user has already
  // backdrop-dismissed this modal (onClose, not onSaved). Without this guard
  // that late resolution would silently force edit mode off with no action
  // from the user, even though they'd already backed out of the editor.
  const dismissedRef = useRef(false);
  useEffect(() => () => {
    dismissedRef.current = true;
  }, []);

  async function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showToast(__('Geef de categorie een naam', 'etm'), 'error');
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await api.categories.create({ name: trimmedName, icon, is_shared: isShared, is_outdoor: isOutdoor });
      } else {
        // update_category() deliberately never touches is_shared/household_id
        // (see its docblock) — reassigning a category between personal and
        // household scope is a bigger decision than this form makes, so
        // don't send a field the backend would silently ignore.
        await api.categories.update(editingCategory.id, { name: trimmedName, icon, is_outdoor: isOutdoor });
      }
      await loadCategories();
      if (!dismissedRef.current) onSaved();
      showToast(__('Categorie opgeslagen', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(__('Categorie verwijderen?', 'etm'))) return;
    setSaving(true);
    try {
      await api.categories.remove(editingCategory.id);
      await loadCategories();
      if (!dismissedRef.current) onSaved();
      showToast(__('Categorie verwijderd', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isCreate ? __('Nieuwe categorie', 'etm') : __('Categorie bewerken', 'etm')}
      onClose={onClose}
    >
      <div class="etm-form-group">
        <label class="etm-form-label" for="etm-category-name">
          {__('Naam', 'etm')}
        </label>
        <input
          id="etm-category-name"
          type="text"
          class="etm-form-input"
          value={name}
          required
          onInput={(e) => setName(e.currentTarget.value)}
        />
      </div>

      <div class="etm-form-group">
        <span class="etm-form-label">{__('Icoon', 'etm')}</span>
        <div class="etm-icon-choice-grid">
          {CATEGORY_ICON_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              class={`etm-icon-choice${choice === icon ? ' active' : ''}`}
              onClick={() => setIcon(choice)}
            >
              <Icon name={choice} size={24} />
            </button>
          ))}
        </div>
      </div>

      {/* Only offered on create — update_category() can't change is_shared
          once a category exists, see the comment in handleSave() above. */}
      {isCreate && household.value ? (
        <div class="etm-form-group etm-checkbox-group">
          <input
            id="etm-category-shared"
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.currentTarget.checked)}
          />
          <label for="etm-category-shared">{__('Gedeeld met huishouden', 'etm')}</label>
        </div>
      ) : null}

      <div class="etm-form-group etm-checkbox-group">
        <input
          id="etm-category-outdoor"
          type="checkbox"
          checked={isOutdoor}
          onChange={(e) => setIsOutdoor(e.currentTarget.checked)}
        />
        <label for="etm-category-outdoor">{__('Buiten (weersgevoelig)', 'etm')}</label>
      </div>

      <button type="button" class="etm-btn etm-btn-primary" disabled={saving} onClick={handleSave}>
        {__('Opslaan', 'etm')}
      </button>

      {!isCreate ? (
        <button type="button" class="etm-btn etm-btn-danger" disabled={saving} onClick={handleDelete}>
          {__('Verwijderen', 'etm')}
        </button>
      ) : null}
    </Modal>
  );
}

export default function CategoryScreen({ params }) {
  // WP-7 (brief 7b): reached from Settings → Categorieën, this screen is
  // pure category management, not the start-flow's category picker — it
  // opens straight into edit mode and never navigates into the
  // start-flow (no "Alle categorieën" tile, which is a flow entry point,
  // not a category to manage). params.manage is absent for every other
  // caller, so the normal start-flow rendering is unchanged.
  const isManage = Boolean(params && params.manage);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editMode, setEditMode] = useState(isManage);

  useEffect(() => {
    if (categories.value.length === 0) {
      loadCategories().catch((err) => showToast(friendlyError(err), 'error'));
    }
  }, []);

  // Saving/deleting a category exits edit mode automatically (brief spec);
  // cancelling the modal (onClose, below) deliberately does not, so the user
  // can keep tapping through several categories to edit in one go. In manage
  // mode there's no "normal mode" to fall back to, so editMode stays true.
  function handleSaved() {
    setEditingCategory(null);
    if (!isManage) setEditMode(false);
  }

  return (
    <div>
      {isManage ? (
        <BackButton onClick={goBack} label={__('Instellingen', 'etm')} />
      ) : (
        <Header
          title="ETM"
          actions={
            <button
              type="button"
              class="etm-icon-btn"
              aria-label={__('Instellingen', 'etm')}
              onClick={() => navigate('settings')}
            >
              <Icon name="settings" size={24} />
            </button>
          }
        />
      )}
      <h1 class="etm-screen-title">{isManage ? __('Categorieën', 'etm') : __('Waarvoor?', 'etm')}</h1>
      <p class="etm-screen-subtitle">
        {isManage ? __('Beheer je categorieën', 'etm') : __('Kies een categorie voor je taak', 'etm')}
      </p>

      <div class="etm-selection-grid">
        {/* WP-3: the default/primary choice — the engine picks across every
            category the user can see (categoryId 0 = all-categories mode
            on GET /tasks/next). Deliberately first and full-width; the
            specific categories below stay for when the user has a
            preference. Not a category itself, so it's absent in manage mode. */}
        {!isManage ? (
          <button
            type="button"
            class="etm-selection-card etm-selection-card--all"
            onClick={() => navigate('time', { categoryId: 0, categoryName: __('Alle categorieën', 'etm') })}
          >
            <span class="etm-category-icon">
              <Icon name="star_shine" size={28} />
            </span>
            <span class="label">{__('Alle categorieën', 'etm')}</span>
            <span class="sublabel">{__('Laat ETM kiezen', 'etm')}</span>
          </button>
        ) : null}

        {categories.value.map((cat) =>
          editMode ? (
            <button
              key={cat.id}
              type="button"
              class="etm-selection-card"
              aria-label={`${__('Categorie bewerken', 'etm')}: ${cat.name}`}
              onClick={() => setEditingCategory(cat)}
            >
              <span class="etm-selection-card__edit-indicator">
                <Icon name="edit" size={16} />
              </span>
              <span class="etm-category-icon">
                <Icon name={categoryIconName(cat.icon)} size={28} />
              </span>
              <span class="label">{cat.name}</span>
            </button>
          ) : (
            <button
              key={cat.id}
              type="button"
              class="etm-selection-card"
              onClick={() => navigate('time', { categoryId: cat.id, categoryName: cat.name })}
            >
              <span class="etm-category-icon">
                <Icon name={categoryIconName(cat.icon)} size={28} />
              </span>
              <span class="label">{cat.name}</span>
            </button>
          )
        )}

        <button
          type="button"
          class="etm-selection-card etm-selection-card--add"
          onClick={() => setEditingCategory({})}
        >
          <span class="etm-category-icon">
            <Icon name="add" size={28} />
          </span>
          <span class="label">{__('Nieuwe categorie', 'etm')}</span>
        </button>
      </div>

      {/* In manage mode edit mode is permanent (there's no start-flow "normal
          mode" to toggle back to) — BackButton above is the only way out,
          straight back to Settings, so this toggle only makes sense from the
          start flow. */}
      {!isManage && categories.value.length > 0 ? (
        <button
          type="button"
          class="etm-btn etm-btn-ghost etm-btn-sm etm-categories-edit-toggle"
          onClick={() => setEditMode((v) => !v)}
        >
          <Icon name={editMode ? 'check' : 'edit'} size={18} />
          {editMode ? __('Klaar', 'etm') : __('Categorieën bewerken', 'etm')}
        </button>
      ) : null}

      {editingCategory ? (
        <CategoryEditModal
          editingCategory={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
}
